import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './Signup.module.css';
import { authService } from '../../../services/api';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '+1'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Determine the role based on the current URL path
  const currentPath = window.location.pathname;
  const isHomeOwnerPath = currentPath.startsWith('/homeowner');
  const userRole = isHomeOwnerPath ? 'homeowner' : 'user';

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      // Only allow numbers
      const cleaned = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: cleaned
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Required field validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.phoneNumber.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone validation (now mandatory)
    if (!/^\d{10}$/.test(formData.phoneNumber)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.register({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        countryCode: formData.countryCode,
        role: userRole // Add the role based on URL
      });

      if (response.data.status === 'PENDING_VERIFICATION') {
        navigate('/verify', { 
          state: { 
            email: formData.email,
            nextStep: 'password-setup',
            role: userRole // Pass role to verification page
          }
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Update signup text and title based on role
  const getRoleSpecificText = () => {
    if (isHomeOwnerPath) {
      return {
        title: 'Create Homeowner Account',
        subtitle: 'Join SmartTech ProServe as a Home Owner'
      };
    }
    return {
      title: 'Create Account',
      subtitle: 'Join SmartTech ProServe'
    };
  };

  const roleText = getRoleSpecificText();

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBackground}>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
      </div>
      
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <h2 className={styles.title}>{roleText.title}</h2>
          <p className={styles.authSubtitle}>{roleText.subtitle}</p>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.nameGroup}>
            <div className={styles.formGroup}>
              <div className={styles.inputWrapper}>
                <i className="fas fa-user"></i>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.inputWrapper}>
                <i className="fas fa-user"></i>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <i className="fas fa-envelope"></i>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className={styles.phoneGroup}>
            <div className={styles.formGroup}>
              <div className={styles.inputWrapper}>
                <i className="fas fa-globe"></i>
                <input
                  type="text"
                  id="countryCode"
                  name="countryCode"
                  placeholder="Code"
                  value={formData.countryCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.inputWrapper}>
                <i className="fas fa-phone"></i>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {error && (
            <div className={styles.error}>
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {/* Terms of Service Agreement */}
          <div className={styles.termsAgreement}>
            <p className={styles.termsText}>
              By signing up, you agree to our{' '}
              <Link to="/terms" target="_blank" className={styles.termsLink}>
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" className={styles.termsLink}>
                Privacy Policy
              </Link>
            </p>
          </div>

          <button 
            type="submit" 
            className={`${styles.authButton} ${loading ? styles.loading : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                <span>Creating Account...</span>
              </>
            ) : (
              <>
                <span>Create Account</span>
                <i className="fas fa-arrow-right"></i>
              </>
            )}
          </button>
        </form>
        
        <div className={styles.authLinks}>
          <div className={styles.divider}>
            <span>or</span>
          </div>
          <p className={styles.authRedirect}>
            Already have an account?{' '}
            <Link to={isHomeOwnerPath ? "/homeowner/login" : "/login"}>Log in now</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup; 